import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation, Navigate } from "react-router-dom";
import { Home, CalendarDays, BarChart3, Users, Bell, User, Settings, History, LogOut, ServerCog, FlaskConical, ShieldCheck, Tag } from "lucide-react";
import { InstallNudge, isStandalone } from "@/components/InstallGuide";
import { syncPush } from "@/components/PushToggle";
import { Logo } from "@/components/Logo";
import { Avatar } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BrandBackdrop, BrandHero, BrandStrip, BrandLogo } from "@/components/BrandBackdrop";
import { brandVars } from "@/lib/brand";
import { ModeToggle } from "@/lib/mode";
import { useNewOffers } from "@/hooks/useNewOffers";
import { getTenant, leaveTenant, enterTenant, tenantIsAuto, TENANT_BLOCKED } from "@/lib/tenant";
import { Trophy, Info } from "lucide-react";

const Dot = ({ testId }) => <span role="status" className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[#B7F52A] ring-2 ring-[#0B1020]" aria-label="Nouvelle offre" data-testid={testId} />;

const MAIN = [
  { to: "/tableau-de-bord", label: "Accueil", icon: Home, id: "home" },
  { to: "/matchs", label: "Matchs", icon: CalendarDays, id: "matches" },
  { to: "/classement", label: "Classement", icon: BarChart3, id: "ranking" },
  { to: "/ligues", label: "Ma ligue", icon: Users, id: "league" },
];
const SECONDARY = [
  { to: "/mes-predictions", label: "Mes prédictions", icon: History, id: "predictions" },
  { to: "/offres", label: "Offres partenaires", icon: Tag, id: "offers" },
  { to: "/profil", label: "Mon profil", icon: User, id: "profile" },
  { to: "/profil?onglet=parametres", label: "Paramètres", icon: Settings, id: "settings" },
];

const SideLink = ({ item, activeFn = (a) => a, dot = false }) => (
  <NavLink
    to={item.to}
    data-testid={`side-nav-${item.id}`}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${activeFn(isActive) ? "bg-[#B7F52A]/10 text-[#B7F52A]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`
    }
  >
    <span className="relative"><item.icon size={18} />{dot && <Dot testId={`side-nav-${item.id}-dot`} />}</span> {item.label}
  </NavLink>
);

function useUnread(skip) {
  const [unread, setUnread] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    if (skip) return;
    api.get("/me/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, [loc.pathname, skip]);
  return unread;
}

const isRanking = (path) => path === "/classement" || /^\/ligues\/[^/]+\/(classement|membre-)/.test(path);

function TermsBanner() {
  const { user, refreshUser } = useAuth();
  if (!user.terms_outdated) return null;
  const accept = async () => { await api.post("/me/terms/accept").catch(() => {}); refreshUser(); };
  return (
    <div className="mb-4 rounded-2xl border border-[#B7F52A]/30 bg-[#B7F52A]/[0.06] p-4 text-xs text-slate-200" data-testid="terms-update-banner">
      <p className="font-semibold text-white">Nos conditions évoluent</p>
      <p className="mt-1">Ma Petite Ligue peut désormais présenter des offres partenaires, toujours identifiées comme telles. Tes données ne sont jamais vendues ni transmises aux partenaires. Les choix publicitaires restent facultatifs dans Paramètres.</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button onClick={accept} className="mpl-btn-primary px-3 py-1.5 text-xs" data-testid="terms-accept">J'ai lu et j'accepte les conditions</button>
        <Link to="/conditions" className="underline">Conditions d'utilisation</Link><Link to="/confidentialite" className="underline">Politique de confidentialité</Link>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout, config, setUser, currentLeague, leagues } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    if (isStandalone() && !user.setup?.installed) { api.patch("/me", { setup: { installed: true } }).then(({ data }) => setUser(data)).catch(() => {}); }
  }, [user.setup?.installed, setUser]);
  useEffect(() => { syncPush().catch(() => {}); }, [user.id]);
  const active = (i, isActive) => (i.id === "ranking" ? isRanking(pathname) : i.id === "league" ? isActive && !isRanking(pathname) : isActive);
  const unread = useUnread(user.role === "partner");
  const newOffers = useNewOffers(user);  const pathLeague = pathname.match(/^\/ligues\/([^/]+)/)?.[1];
  const tenantId = getTenant();
  const tenant = tenantId ? leagues.find((l) => l.id === tenantId) : null;
  useEffect(() => {
    if (tenantId && leagues.length && !tenant) leaveTenant();
    else if (!tenantId && leagues.length && leagues.every((l) => l.league_type === "BUSINESS") && user.role !== "superadmin") enterTenant(leagues[0].id, pathname, true);
  }, [tenantId, tenant, leagues, user.role, pathname]);
  const main = tenant ? [{ to: "/tableau-de-bord", label: "Accueil", icon: Home, id: "home" }, { to: "/matchs", label: "Pronos", icon: CalendarDays, id: "matches" },
    { to: "/classement", label: "Classement", icon: BarChart3, id: "ranking" }, { to: `/ligues/${tenant.id}/defis`, label: "Défis", icon: Trophy, id: "challenges" }] : MAIN;
  const secondary = tenant ? [{ to: `/ligues/${tenant.id}/membres`, label: "Collègues", icon: Users, id: "colleagues" }, { to: `/ligues/${tenant.id}/apercu`, label: "Infos", icon: Info, id: "info" }, ...SECONDARY.filter((i) => i.id !== "offers")] : SECONDARY;
  const shown = pathLeague ? leagues.find((l) => l.id === pathLeague) : currentLeague;
  const brand = !pathname.startsWith("/admin") ? shown?.brand : null;
  useEffect(() => {
    const root = document.documentElement;
    const vars = brandVars(brand);
    root.classList.toggle("mpl-branded", !!brand);
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    const meta = document.querySelector('meta[name="theme-color"]');
    const prev = meta?.getAttribute("content");
    if (meta && brand) meta.setAttribute("content", brand.secondary);
    return () => { root.classList.remove("mpl-branded"); Object.keys(vars).forEach((k) => root.style.removeProperty(k)); if (meta && prev) meta.setAttribute("content", prev); };
  }, [brand]);
  if (user.role === "partner") return <Navigate to="/partenaire" replace />;
  if (tenant && TENANT_BLOCKED.some((r) => r.test(pathname))) return <Navigate to="/tableau-de-bord" replace />;
  return (
    <div className={`mpl-grain relative isolate min-h-screen bg-[#0B1020] text-white ${brand ? "mpl-branded" : ""}`} style={brandVars(brand)} data-testid="app-shell">
      <BrandBackdrop brand={brand} />
      {config?.is_demo && (
        <div data-testid="demo-mode-banner" className="relative z-40 flex items-center justify-center gap-2 border-b border-amber-400/20 bg-amber-400/[0.07] px-4 py-1.5 text-[11px] font-semibold text-amber-200">
          <FlaskConical size={12} /> Mode démonstration : équipes et résultats fictifs, aucune donnée NBA officielle
        </div>
      )}
      <aside className="fixed bottom-0 left-0 top-0 z-[45] hidden w-64 flex-col border-r border-white/[0.06] bg-[#05070D] px-4 py-6 lg:flex" style={{ top: config?.is_demo ? 29 : 0 }}>
        <Link to="/tableau-de-bord" className="mb-8 px-2">{brand ? <BrandLogo brand={brand} name={shown.name} /> : <Logo />}</Link>
        <nav className="flex flex-col gap-1">{main.map((i) => <SideLink key={i.to} item={i} activeFn={(a) => active(i, a)} />)}</nav>
        <div className="my-5 h-px bg-white/[0.06]" />
        <nav className="flex flex-col gap-1">
          {secondary.map((i) => <SideLink key={i.to} item={i} dot={i.id === "offers" && newOffers} activeFn={(a) => {
            const settings = pathname === "/parametres" || (pathname === "/profil" && new URLSearchParams(window.location.search).get("onglet") === "parametres");
            return i.id === "settings" ? settings : i.id === "profile" ? a && !settings : a;
          }} />)}
          {user?.role === "superadmin" && <SideLink item={{ to: "/admin", label: "Administration", icon: ShieldCheck, id: "superadmin" }} activeFn={(a) => a && !pathname.startsWith("/admin/synchronisation")} />}
          {user?.role === "superadmin" && <SideLink item={{ to: "/admin/synchronisation", label: "Synchronisation", icon: ServerCog, id: "admin" }} />}
        </nav>
        {tenant && !tenantIsAuto() && <button onClick={leaveTenant} className="mt-4 rounded-xl px-3 py-2 text-left text-xs text-slate-400 hover:bg-white/5" data-testid="tenant-leave">Quitter l'espace entreprise</button>}
        <button onClick={logout} data-testid="logout-button" className="mt-auto flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-white/5 hover:text-white">
          <LogOut size={18} /> Déconnexion
        </button>
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/[0.06] bg-[#0B1020]/85 px-4 py-3 backdrop-blur-xl lg:pl-72 lg:pr-8"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))", paddingLeft: "max(1rem, env(safe-area-inset-left))", paddingRight: "max(1rem, env(safe-area-inset-right))" }} data-testid="app-header">
        <Link to="/tableau-de-bord" className="min-w-0 lg:hidden">{brand ? <BrandLogo brand={brand} name={shown.name} size={28} /> : <Logo size={28} />}</Link>
        <div className="hidden lg:block" />
        <div className="flex items-center gap-2">
          <ModeToggle compact testId="header-mode-toggle" />
          {user?.role === "superadmin" && (
            <Link to="/admin" aria-label="Administration" data-testid="header-admin-link"
              className={`flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${pathname.startsWith("/admin") ? "border-[#B7F52A] text-[#B7F52A]" : "border-white/10 hover:bg-white/5"}`}>
              <ShieldCheck size={18} />
            </Link>
          )}
          {!tenant && <Link to="/offres" aria-label="Offres partenaires" title="Offres partenaires" data-testid="header-offers-link"
            className={`relative flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${pathname.startsWith("/offres") ? "border-[#B7F52A] text-[#B7F52A]" : "border-white/10 hover:bg-white/5"}`}>
            <Tag size={18} />{newOffers && <Dot testId="header-offers-dot" />}
          </Link>}
          <Link to="/notifications" aria-label="Notifications" data-testid="notifications-link" className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/10 hover:bg-white/5">
            <Bell size={18} />
            {unread > 0 && (
              <span data-testid="notifications-unread" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B7F52A] px-1 text-[10px] font-bold text-[#05070D]">{unread}</span>
            )}
          </Link>
          {tenant && !tenantIsAuto() && <button onClick={leaveTenant} aria-label="Quitter l'espace entreprise" title="Quitter l'espace entreprise" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 hover:bg-white/5 lg:hidden" data-testid="tenant-leave-mobile"><LogOut size={16} /></button>}
          <Link to="/profil" aria-label="Mon profil" data-testid="header-profile-link" className="rounded-full"><Avatar user={user} size={38} /></Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-28 pt-6 sm:px-6 lg:ml-64 lg:max-w-none lg:px-10 lg:pb-12">
        <div className={`mx-auto ${pathname.startsWith("/admin") ? "max-w-7xl" : "max-w-3xl"}`}>
          {config?.announcement && <p className="mb-4 rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-xs text-slate-200" data-testid="platform-announcement">{config.announcement}</p>}
          <InstallNudge />
          <TermsBanner />
          {brand && !pathLeague && (pathname === "/tableau-de-bord" ? <BrandHero brand={brand} name={currentLeague.name} /> : <BrandStrip brand={brand} name={currentLeague.name} />)}
          <Outlet />
        </div>
      </main>

      <nav data-testid="bottom-nav" className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#05070D]/90 px-2 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden">
        <div className="mx-auto grid max-w-md grid-cols-4">
          {main.map((i) => (
            <NavLink key={i.to} to={i.to} data-testid={`nav-${i.id}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${active(i, isActive) ? "text-[#B7F52A]" : "text-slate-400"}`}>
              <i.icon size={21} /> {i.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
